import type { SDKState } from "@/state/sdk-state";
import { EmbedIframe } from "@/ui/embed-iframe";
import { EmbedBroker, EmbedMessageType } from "./embed-broker";
import { joinURL } from "@/utils/url";
import { Logger } from "@/utils/logger";

export const EMBED_CONTAINER_ID = "fanfest-embed";
export const EMBED_ELEMENT_ID = "fanfest-embed-element";

export class EmbedService {
  #state: SDKState;
  #trackedContainers: Set<HTMLElement> = new Set();
  #embedElements: Set<HTMLIFrameElement> = new Set();
  broker: EmbedBroker;
  #logger: Logger;

  constructor(state: SDKState, logger: Logger, broker?: EmbedBroker) {
    this.#state = state;
    this.#logger = logger.extend(this.constructor.name);
    this.broker = broker ?? new EmbedBroker(state, this.#logger);
  }

  public init() {
    this.#state.channelInfo.value.subject.subscribe(
      (channelInfo) => {
        if (!channelInfo?.channel_id) return;

        this.#findElementsInContainer(document.body, channelInfo.channel_id);
        this.#setupMutationObserver(channelInfo.channel_id);
        this.broker.init();
      },
      { notifyOnSubscribe: true },
    );

    this.#state.authState.value.subject.subscribe(
      (result) => {
        if (this.#embedElements.size === 0) return;

        for (const element of this.#embedElements) {
          this.broker.sendMessage(
            {
              type: EmbedMessageType.Authentication,
              jwt: result?.authToken ?? null,
              matches_identity: result?.user !== null,
              auth_channel: result?.authChannelType ?? null,
              validated_identifiers: result?.validatedIdentifiers ?? [],
            },
            element,
          );
        }
      },
      { notifyOnSubscribe: false },
    );

    this.broker.onMessage.subscribe((message) => {
      switch (message.type) {
        case EmbedMessageType.AuthenticationFinished: {
          const currentAuthState = this.#state.authState.value.get();
          if (currentAuthState?.user === null) {
            this.#state.authState.value.set({
              ...currentAuthState,
              user: message.user,
            });
          } else if (currentAuthState === null) {
            this.#logger.warn(
              "[EmbedService] Authentication finished message received but no auth state is set, ignoring",
            );
          } else {
            this.#logger.warn(
              "[EmbedService] Authentication finished message received but user is already authenticated, ignoring",
            );
          }
          break;
        }
        case EmbedMessageType.EmbedReady: {
          for (const element of this.#embedElements) {
            const result = this.#state.authState.value.get();

            this.broker.sendMessage(
              {
                type: EmbedMessageType.Authentication,
                jwt: result?.authToken ?? null,
                matches_identity: result?.user !== null,
                auth_channel: result?.authChannelType ?? null,
                validated_identifiers: result?.validatedIdentifiers ?? [],
              },
              element,
            );
          }
          break;
        }
      }
    });
  }

  detach() {
    this.broker.detach();
  }

  #findElementsInContainer(container: HTMLElement, channelId: string) {
    const element = container.querySelector(`#${EMBED_CONTAINER_ID}`);
    if (element && element instanceof HTMLElement) {
      this.#trackContainer(element, channelId);
    }
  }

  #setupMutationObserver(channelId: string) {
    const handleMutation = (mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        // Skip if the target is an embed element, otherwise an infinite loop will occur
        if (
          mutation.target instanceof HTMLElement &&
          this.#embedElements.has(mutation.target as HTMLIFrameElement)
        ) {
          continue;
        }

        if (
          mutation.attributeName === "id" &&
          mutation.target instanceof HTMLElement
        ) {
          const element = mutation.target as HTMLElement;
          if (element.id === EMBED_CONTAINER_ID) {
            this.#trackContainer(element, channelId);
          }
        }

        if (mutation.addedNodes) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (!(node instanceof HTMLElement)) continue;

            if (node.id === EMBED_CONTAINER_ID) {
              this.#trackContainer(node, channelId);
            } else if (node.childElementCount > 0) {
              this.#findElementsInContainer(node, channelId);
            }
          }
        }

        if (mutation.removedNodes.length > 0) {
          for (const node of Array.from(mutation.removedNodes)) {
            if (node instanceof HTMLElement && node.id === EMBED_CONTAINER_ID) {
              this.#untrackContainer(node);
            }
          }
        }
      }
    };

    const observer = new MutationObserver(handleMutation);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id"],
    });
  }

  #trackContainer(element: HTMLElement, channelId: string) {
    if (this.#trackedContainers.has(element)) {
      return;
    }

    this.#trackedContainers.add(element);
    this.#setupElement(element, channelId);
  }

  #untrackContainer(element: HTMLElement) {
    if (!this.#trackedContainers.has(element)) {
      return;
    }

    this.#trackedContainers.delete(element);
    this.#embedElements.delete(element as HTMLIFrameElement);
  }

  #setupElement(element: HTMLElement, channelId: string) {
    const iframe = this.#createIframe(channelId);
    element.innerHTML = "";

    this.#embedElements.add(iframe);
    element.appendChild(iframe);
  }

  #createIframe(channelId: string) {
    const src = new URL(joinURL(this.#state.options.appOrigin, channelId));
    src.searchParams.set("embed", "true");

    return EmbedIframe({
      id: EMBED_ELEMENT_ID,
      src: src.toString(),
      backgroundColor: this.#state.options.iframe.backgroundColor,
    });
  }
}
